import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import BadgeIcon from '@mui/icons-material/Badge';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useTranslation } from 'react-i18next';
import { brand, radius, shadow } from './brand';

const float = keyframes`
    0%, 100% { transform: translateY(0) rotate(var(--tilt, 0deg)); }
    50%      { transform: translateY(-14px) rotate(var(--tilt, 0deg)); }
`;

// Each badge sits on the corner of the card it belongs to, like on credenco.com:
// the ID badge with the Personal ID, the car with the driving licence, the euro
// with the IBAN card. They render inside the card so they float along with it.
const cards = [
    {
        image: `${process.env.PUBLIC_URL}/rijkskaart.png`,
        issuer: 'Rijksoverheid',
        nameKey: 'hero.cards.pid',
        top: '2%',
        left: '46%',
        width: '46%',
        tilt: '2deg',
        duration: '7s',
        delay: '0s',
        badgeIcon: <BadgeIcon sx={{fontSize: 18}}/>,
        badgePosition: {top: -16, right: -16},
    },
    {
        image: `${process.env.PUBLIC_URL}/dutch-driving-licence.png`,
        issuer: 'RDW',
        nameKey: 'hero.cards.licence',
        top: '38%',
        left: '4%',
        width: '44%',
        tilt: '-3deg',
        duration: '8.5s',
        delay: '.8s',
        badgeIcon: <DirectionsCarIcon sx={{fontSize: 18}}/>,
        badgePosition: {top: -16, left: -16},
    },
    {
        image: `${process.env.PUBLIC_URL}/tulip-card-background.jpg`,
        issuer: 'Tulip Trust Bank',
        nameKey: 'hero.cards.bank',
        top: '62%',
        left: '48%',
        width: '42%',
        tilt: '3deg',
        duration: '9.5s',
        delay: '1.6s',
        badgeIcon: <EuroSymbolIcon sx={{fontSize: 18}}/>,
        badgePosition: {bottom: -16, right: -16},
    },
];

const noMotion = {'@media (prefers-reduced-motion: reduce)': {animation: 'none'}};

export const FloatingCards: FC = () => {
    const {t} = useTranslation();
    return (
    <Box aria-hidden="true" sx={{position: 'relative', width: '100%', paddingTop: '96%'}}>

        {/* The device the cards hover over. */}
        <Box sx={{
            position: 'absolute',
            inset: '8% 14% 4% 14%',
            borderRadius: '32px',
            background: `linear-gradient(160deg, ${brand.panelDeep} 0%, ${brand.blue} 55%, ${brand.accentSoft} 130%)`,
            border: '6px solid rgba(3, 19, 61, .55)',
            boxShadow: shadow.lifted,
        }}/>

        {cards.map(card => (
            <Box
                key={card.nameKey}
                style={{'--tilt': card.tilt} as React.CSSProperties}
                sx={{
                    position: 'absolute',
                    top: card.top,
                    left: card.left,
                    width: card.width,
                    animation: `${float} ${card.duration} ease-in-out ${card.delay} infinite`,
                    ...noMotion,
                }}>
                <Box sx={{borderRadius: radius.card, overflow: 'hidden', boxShadow: shadow.lifted}}>
                    <Box
                        component="img"
                        src={card.image}
                        alt=""
                        sx={{display: 'block', width: '100%', height: {xs: 82, lg: 104}, objectFit: 'cover'}}/>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        gap: '8px',
                        padding: '8px 10px',
                        background: 'rgba(3, 19, 61, .82)',
                    }}>
                        <Box sx={{minWidth: 0}}>
                            <Typography sx={{color: 'rgba(255,255,255,.72)', fontSize: '.62rem', lineHeight: 1.3}}>
                                {card.issuer}
                            </Typography>
                            <Typography sx={{color: '#FFFFFF', fontSize: '.72rem', fontWeight: 600, lineHeight: 1.3}}>
                                {t(card.nameKey)}
                            </Typography>
                        </Box>
                        <Box sx={{
                            background: 'rgba(255,255,255,.16)',
                            borderRadius: radius.pill,
                            padding: '2px 9px',
                            color: '#FFFFFF',
                            fontSize: '.62rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}>
                            {t('hero.cards.share')}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{
                    position: 'absolute',
                    ...card.badgePosition,
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    background: brand.navy,
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: shadow.lifted,
                }}>
                    {card.badgeIcon}
                </Box>
            </Box>
        ))}

    </Box>
);
};
